import type { UnlockId } from '@/core/content/ids';
import { unlockStatus } from '@/core/domain/unlocks';
import { useBack } from '@/ui/back/backStack';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSave, useServices } from '@/ui/services/ServicesContext';
import { newTxId } from '@/ui/util/ids';
import styles from './UnlockModal.module.css';
import { SpotArt } from '@/ui/art/SpotArt';

/**
 * S15 in its "mở rộng" form (ux-screens §3.15): a paid unlock reached its star goal. Main button
 * "Mở rộng với n xu", secondary "Để sau" — always reachable again from the sign in the scene.
 */
export function ExpansionModal({
  unlockId,
  onClose,
  onExpanded,
}: {
  unlockId: UnlockId;
  onClose: () => void;
  onExpanded: (unlockId: UnlockId) => void;
}) {
  const { t } = useI18n();
  const { content, dispatch } = useServices();
  useBack(onClose);
  const def = content.unlocks.get(unlockId);
  const status = useSave((s) => {
    const st = unlockStatus(content, s, unlockId);
    return st.kind === 'purchasable' ? `${String(st.cost)}:${String(st.affordable)}` : st.kind;
  });
  const coins = useSave((s) => s.wallet.coins);
  if (!def || !status.includes(':')) return null;
  const [costText, affordable] = status.split(':');
  const cost = Number(costText);

  return (
    <div className={styles.scrim}>
      <section
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="expand-title"
        data-testid="expansion-modal"
      >
        <div className={styles.art}>
          <SpotArt name="ill_unlock" fallback="unlocks" size={150} />
        </div>
        <h2 id="expand-title" className={styles.title}>
          {t('ui.unlock.title')}
        </h2>
        <p className={styles.opened}>{t('ui.expand.ready', { name: t(def.nameKey) })}</p>
        <ul className={styles.lines}>
          <li>{t(`ui.expand.desc.${unlockId}`)}</li>
        </ul>
        {affordable !== 'true' && <p className={styles.next}>{t('ui.expand.short', { n: cost - coins })}</p>}
        <div className={styles.actions}>
          <button type="button" className={styles.secondary} onClick={onClose}>
            {t('ui.unlock.later')}
          </button>
          <button
            type="button"
            className={styles.primary}
            data-testid="expansion-pay"
            disabled={affordable !== 'true'}
            onClick={() => {
              const r = dispatch({ type: 'payUnlock', txId: newTxId(), unlockId });
              if (r.ok) onExpanded(unlockId);
            }}
          >
            {t(def.grants.some((g) => g.type === 'scene') ? 'ui.unlock.restore' : 'ui.unlock.expand', {
              price: cost,
            })}
          </button>
        </div>
      </section>
    </div>
  );
}
